import { t } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useUpdateSessionSettingsMutation } from '@/core/services/session-settings'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'

const schema = z.object({
  inactivityTimeoutMinutes: z.number().int().min(1).max(120),
})

export type SessionSettingsFormData = z.infer<typeof schema>

type UseSessionSettingsFormParams = {
  initialValues?: SessionSettingsFormData
}

export function useSessionSettingsForm({ initialValues }: UseSessionSettingsFormParams) {
  const { enqueueSnackbar } = useSnackbar()

  const [updateSettings, { isLoading: isSubmitting }] = useUpdateSessionSettingsMutation()

  const rhForm = useForm<SessionSettingsFormData>({
    defaultValues: initialValues ?? { inactivityTimeoutMinutes: 20 },
    resolver: zodResolver(schema),
  })
  const { reset } = rhForm

  useEffect(() => {
    reset(initialValues)
  }, [initialValues, reset])

  const submitHandler: SubmitHandler<SessionSettingsFormData> = async (data) => {
    try {
      await updateSettings({ inactivityTimeoutMinutes: data.inactivityTimeoutMinutes }).unwrap()
      enqueueSnackbar(t`Session settings updated successfully`, { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(t`Unable to update session settings`, { variant: 'error' })
    }
  }

  return { isSubmitting, rhForm, submitHandler }
}
